import { Reveal } from "@/components/Reveal";
import { FEATURES } from "@/lib/brand";
import { ShieldCheck } from "lucide-react";

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <Reveal>
            <p className="overline text-[#2BBFB3]">Features</p>
            <h2 className="mt-5 max-w-2xl font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Everything an instructor needs. Nothing they don't.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-[#2B2B2B]/60">
              Purpose-built around the way you already teach — and the way learners and parents want to see progress.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-px rounded-3xl overflow-hidden border border-[#E5E5E5] bg-[#E5E5E5]">
          {FEATURES.map((f, i) => (
            <Reveal
              key={f.title}
              delay={(i % 3) * 0.08}
              className={f.span || ""}
            >
              <div
                data-testid={`feature-${i}`}
                className={`group relative h-full bg-white hover:bg-[#F7F7F7] transition-colors ${
                  f.featured ? "p-8 sm:p-12" : "p-8"
                } ${f.pro ? "border-t-2 border-t-[#F5A623]" : ""}`}
              >
                <div className={f.featured ? "lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center" : ""}>
                  <div>
                    <div className="flex items-center justify-between">
                      <span
                        className={`grid place-items-center rounded-xl transition-colors ${
                          f.featured ? "w-14 h-14" : "w-12 h-12"
                        } ${
                          f.pro
                            ? "bg-[#F5A623]/15 text-[#F5A623]"
                            : "bg-[#2BBFB3]/12 text-[#1FA097] group-hover:bg-[#2BBFB3] group-hover:text-white"
                        }`}
                      >
                        <f.icon size={f.featured ? 26 : 22} />
                      </span>
                      <span
                        data-testid={f.featured ? "feature-records-tag" : undefined}
                        className={`font-semibold uppercase tracking-widest rounded-full ${
                          f.featured ? "text-[11px] px-3.5 py-1.5" : "text-[10px] px-2.5 py-1"
                        } ${
                          f.pro ? "bg-[#F5A623]/12 text-[#B57608]" : "bg-[#2B2B2B]/5 text-[#2B2B2B]/50"
                        }`}
                      >
                        {f.tag}
                      </span>
                    </div>
                    <h3 className={`mt-6 font-display font-bold text-[#2B2B2B] ${f.featured ? "text-3xl sm:text-4xl tracking-tight" : "text-2xl"}`}>
                      {f.title}
                    </h3>
                    <p
                      data-testid={f.featured ? "feature-records-copy" : undefined}
                      className={`mt-3 text-[#2B2B2B]/65 leading-relaxed ${f.featured ? "text-lg mt-5" : ""}`}
                    >
                      {f.desc}
                    </p>
                  </div>
                  {f.protection && (
                    <div
                      data-testid="feature-records-protection"
                      className="mt-8 lg:mt-0 rounded-2xl bg-[#2B2B2B] p-7 sm:p-9 border-l-4 border-[#F5A623]"
                    >
                      <p className="flex items-center gap-2.5 font-display font-extrabold tracking-tight text-2xl sm:text-3xl text-white">
                        <ShieldCheck size={26} className="text-[#F5A623] shrink-0" />
                        {f.protection.heading}
                      </p>
                      <p className="mt-4 text-white/70 leading-relaxed">{f.protection.sub}</p>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
